import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { formatPrice } from "@/components/ProductCard";
import { toast } from "sonner";
import { Receipt, UploadSimple, CaretDown, PaperPlaneRight, DownloadSimple, CheckCircle } from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

function InvoiceDetail({ order, onUpdate }) {
  const inv = order.invoice || {};
  const f = inv.fiscal || {};
  const [uploading, setUploading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const fileRef = useRef();
  const issued = inv.status === "issued";

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/orders/${order.id}/invoice/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Archivo subido");
      const r = await api.get(`/admin/invoices`);
      onUpdate(r.data.find((o) => o.id === order.id) || order);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const issue = async () => {
    setIssuing(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/invoice/issue`);
      toast.success("Factura enviada al cliente");
      onUpdate({ ...order, invoice: r.data });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setIssuing(false); }
  };

  const Row = ({ label, value }) => (<div><span className="text-[10px] text-muted-foreground uppercase tracking-wide block">{label}</span><span className="text-sm">{value || "—"}</span></div>);

  return (
    <div className="border-t border-black/10 p-4 space-y-4 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Datos fiscales</p>
          <div className="grid grid-cols-2 gap-2">
            <Row label="RFC" value={f.rfc} />
            <Row label="CP" value={f.zip} />
            <div className="col-span-2"><Row label="Nombre / Razón social" value={f.business_name} /></div>
            <Row label="Régimen fiscal" value={f.tax_regime} />
            <Row label="Uso CFDI" value={f.cfdi_use} />
            <div className="col-span-2"><Row label="Correo" value={f.email} /></div>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Pedido</p>
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <img src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className="w-8 h-8 rounded object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
              <span className="text-xs">{it.product_title} ×{it.quantity}</span>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-2">Total: <b>{formatPrice(order.total)}</b></p>
          <p className="text-xs text-muted-foreground">{order.buyer_name} · {order.buyer_email}</p>
        </div>
      </div>

      {(inv.files || []).length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Archivos de la factura</p>
          <div className="flex flex-wrap gap-2">
            {inv.files.map((file, i) => (
              <a key={i} href={fileUrl(file.path)} target="_blank" rel="noreferrer" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 border border-black/10 rounded px-2 py-1"><DownloadSimple weight="bold" size={13} /> {file.name}</a>
            ))}
          </div>
        </div>
      )}

      {!issued ? (
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf,application/xml,.xml" onChange={upload} className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`upload-invoice-${order.id}`} className="nb-btn bg-secondary px-3 py-2 text-sm flex items-center gap-1"><UploadSimple weight="bold" size={14} /> {uploading ? "Subiendo…" : "Subir factura (PDF/XML)"}</button>
          <button onClick={issue} disabled={issuing || !(inv.files || []).length} data-testid={`issue-invoice-${order.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-2 text-sm flex items-center gap-1 hover:brightness-110 disabled:opacity-50"><PaperPlaneRight weight="bold" size={14} /> {issuing ? "Enviando…" : "Enviar al cliente"}</button>
        </div>
      ) : (
        <p className="text-sm text-[hsl(var(--meli-green))] font-semibold flex items-center gap-1"><CheckCircle weight="fill" size={16} /> Factura enviada al cliente el {new Date(inv.issued_at).toLocaleDateString("es-MX")}</p>
      )}
    </div>
  );
}

export function InvoicesAdminTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("requested");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    const url = filter ? `/admin/invoices?status=${filter}` : "/admin/invoices";
    setOrders((await api.get(url)).data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateOne = (updated) => setOrders((list) => list.map((o) => (o.id === updated.id ? updated : o)));

  const FILTERS = [["requested", "Solicitadas"], ["issued", "Emitidas"], ["", "Todas"]];

  return (
    <div data-testid="admin-invoices">
      <div className="flex gap-1 mb-4 flex-wrap">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`invoice-filter-${k || "all"}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === k ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`}>{label}</button>
        ))}
      </div>
      {loading ? <p className="text-muted-foreground">Cargando…</p> : orders.length === 0 ? (
        <div className="bg-white rounded-lg nb-shadow p-8 text-center text-muted-foreground">Sin facturas</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const issued = o.invoice?.status === "issued";
            return (
              <div key={o.id} className="bg-white rounded-lg nb-shadow overflow-hidden" data-testid={`admin-invoice-${o.id}`}>
                <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-secondary/30">
                  <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Receipt weight="bold" size={18} className="text-primary" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">Pedido {o.order_number}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${issued ? "bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))]" : "bg-amber-100 text-amber-700"}`}>{issued ? "Emitida" : "Solicitada"}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{o.buyer_name} · RFC {o.invoice?.fiscal?.rfc} · {formatPrice(o.total)}</p>
                  </div>
                  <CaretDown weight="bold" size={16} className={`transition-transform ${open === o.id ? "rotate-180" : ""}`} />
                </button>
                {open === o.id && <InvoiceDetail order={o} onUpdate={updateOne} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
